import { I18N, React, variantProvider } from '@/app'
import { Button, Image, Like, PlayerYoutube, StoryAvatar, Text, View } from '@/components'
import { Organisation } from '@/features'
import { AppStatus, shareStoryId } from '@/redux'
import { analytics } from '@/services'
import { Reply, Story } from '@/types'
import { arePropsEqual, PropsOf } from '@codeleap/common'
import { TypeGuards } from '@codeleap/types'
import { TouchableProps, useSearchParams } from '@codeleap/web'
import { memo } from 'react'

type StoriesCardProps = {
  story: Partial<Story>
  onToggleLike?: () => void
  onPress?: TouchableProps['onPress']
  showOrganisation?: boolean
  width?: number
  replies?: Reply[]
  asLink?: boolean
  href?: string
  children?: React.ReactNode
} & Partial<PropsOf<typeof View>>

const IMAGE_HEIGHT = 244

const _StoriesCard = ({
  story,
  onToggleLike,
  showOrganisation,
  width,
  replies,
  asLink = false,
  children,
  ...props
}: StoriesCardProps) => {
  // perf.inspectRender('stories card')

  const [searchParams, setSearchParams] = useSearchParams({
    sources: '',
    storyId: null,
  })

  const headline = story?.headline
  const overview = story?.overview

  const likes = String(story?.likes?.count)
  const isLiked = story?.likes?.is_liked

  const picture = story?.picture

  const showReply = replies?.length > 0

  const handlePressStoryCard = () => {
    analytics.track('story_view', { source: 'Learn' })
    setSearchParams({ ...searchParams, storyId: story?.id })
    AppStatus.setModal('storyDetail')
    shareStoryId.setState({ id: story?.id })
  }

  const storyImg = !!picture ? (
    <Image
      source={picture}
      style={styles.image}
      objectFit='cover'
      alt={'Story Card Image'}
      type={'dynamic'}
      variants={['fullWidth', 'border-radius:small']}
    />
  ) : null

  return (
    <View tabIndex={0} css={{ width }} {...props} style={styles.wrapper}>
      {children}

      <StoryAvatar story={story} showOrganisation={showOrganisation} />

      {!!story?.video ? <PlayerYoutube.Preview video={story?.video} /> : storyImg}

      <View variants={['column', 'gap:0.5']}>
        <Text variants={['color:neutral10', 'bold', 'h4', 'wordBreak-word']} text={headline} component='h3' />

        <Text variants={['p1', 'wordBreak-word']} text={overview} />
      </View>

      {showReply && <Organisation.ReplyPreview replies={replies} style={styles.replyPreview} />}

      <View variants={['row', 'alignCenter', 'justifySpaceBetween', 'fullWidth']}>
        {TypeGuards.isFunction(onToggleLike) ? (
          <Like
            isLiked={isLiked}
            likesCount={Number(likes)}
            onToggleLike={onToggleLike}
            variants={['row', 'alignCenter', 'gap:1']}
            tabIndex={0}
          />
        ) : (
          <div />
        )}

        <Button
          debugName='Read more about story'
          text={I18N.t('reusable.readMore')}
          variants={['color:primary3', 'marginLeft:auto', 'minimal', 'story:readMore']}
          onPress={handlePressStoryCard}
          component={asLink ? 'a' : undefined}
          style={{ textDecoration: 'none' }}
        />
      </View>
    </View>
  )
}

export const StoriesCard = memo(_StoriesCard, (prev, next) => {
  return arePropsEqual(prev, next, {
    check: ['story', 'children'],
  })
}) as typeof _StoriesCard

const styles = variantProvider.createComponentStyle(
  theme => ({
    wrapper: {
      ...theme.presets.fullWidth,
      ...theme.presets.column,
      ...theme.presets.alignStart,
      ...theme.presets.justifyCenter,
      ...theme.effects.light,
      ...theme.spacing.gap(2),
      ...theme.spacing.padding(2),
      borderRadius: theme.borderRadius.small,
      backgroundColor: theme.colors.neutral1,
    },
    image: {
      ...theme.presets.fullWidth,
      height: IMAGE_HEIGHT,
    },
    replyPreview: {
      ...theme.spacing.marginTop(-1),
    },
  }),
  true,
)
